export const PARENT_FINANCIAL_AID_POINTER_STORAGE_PREFIX = 'ystnj:parent-financial-aid:pointer:v1';
export const LEGACY_PARENT_FINANCIAL_AID_DRAFT_STORAGE_PREFIX = 'ystnj:parent-financial-aid:draft:v1';
export const PARENT_FINANCIAL_AID_ENTRY_FOCUS_STORAGE_KEY = 'ystnj:parent-financial-aid:entry-focus:v1';

const MAX_STEP_INDEX = 8;
const ENTRY_FOCUS_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export type ParentFinancialAidPointer = {
  applicationId: string | null;
  currentStep: number;
};

type ParentFinancialAidPointerRecord = ParentFinancialAidPointer & {
  version: 1;
  updatedAt: string;
};

type ParentFinancialAidEntryFocusRecord = {
  version: 1;
  focused: true;
  activatedAt: number;
};

const readText = (value: unknown): string => String(value ?? '').trim();

const fingerprintText = (value: string): string => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const normalizePointerScope = (value: unknown): string => {
  const normalized = readText(value).toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return normalized || 'default';
};

export const selectParentFinancialAidPointerStorage = (storage: {
  localStorage?: Storage | null;
  sessionStorage?: Storage | null;
}): Storage | null => storage.localStorage ?? storage.sessionStorage ?? null;

export const clampParentFinancialAidStep = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.min(MAX_STEP_INDEX, Math.max(0, Math.floor(value)));
};

export const resolveParentFinancialAidPointerStorageKey = (
  actorId: string,
  scope?: string,
): string => {
  const actorFingerprint = fingerprintText(readText(actorId).toLowerCase() || 'anonymous-parent');
  return `${PARENT_FINANCIAL_AID_POINTER_STORAGE_PREFIX}:${normalizePointerScope(scope)}:${actorFingerprint}`;
};

export const resolveLegacyParentFinancialAidPointerStorageKey = (actorId: string): string =>
  `${PARENT_FINANCIAL_AID_POINTER_STORAGE_PREFIX}:${readText(actorId) || 'anonymous-parent'}`;

export const readParentFinancialAidPointer = (
  storage: Storage | null,
  storageKey: string,
): ParentFinancialAidPointer | null => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ParentFinancialAidPointerRecord>;
    if (parsed.version !== 1) return null;
    return {
      applicationId: readText(parsed.applicationId) || null,
      currentStep: clampParentFinancialAidStep(parsed.currentStep),
    };
  } catch {
    return null;
  }
};

export const readParentFinancialAidPointerWithFallback = (
  storage: {
    localStorage?: Storage | null;
    sessionStorage?: Storage | null;
  },
  storageKey: string,
  legacyStorageKeys: string[] = [],
): ParentFinancialAidPointer | null => {
  const primary = selectParentFinancialAidPointerStorage(storage);
  const primaryPointer = readParentFinancialAidPointer(primary, storageKey);
  if (primaryPointer) return primaryPointer;

  const fallback = primary === storage.localStorage ? storage.sessionStorage : storage.localStorage;
  const fallbackPointer = readParentFinancialAidPointer(fallback ?? null, storageKey);
  if (fallbackPointer && primary && fallback !== primary) {
    writeParentFinancialAidPointer(primary, storageKey, fallbackPointer);
  }
  if (fallbackPointer) return fallbackPointer;

  const stores = [primary, fallback ?? null].filter((entry, index, entries): entry is Storage =>
    Boolean(entry) && entries.indexOf(entry) === index);
  for (const legacyStorageKey of legacyStorageKeys) {
    for (const legacyStorage of stores) {
      const legacyPointer = readParentFinancialAidPointer(legacyStorage, legacyStorageKey);
      if (!legacyPointer) continue;
      if (primary) {
        writeParentFinancialAidPointer(primary, storageKey, legacyPointer);
      }
      stores.forEach((entry) => {
        try {
          entry.removeItem(legacyStorageKey);
        } catch {
          // Ignore best-effort cleanup failures for legacy pointer migration.
        }
      });
      return legacyPointer;
    }
  }

  return null;
};

export const writeParentFinancialAidPointer = (
  storage: Storage | null,
  storageKey: string,
  pointer: ParentFinancialAidPointer,
): void => {
  if (!storage) return;
  try {
    const record: ParentFinancialAidPointerRecord = {
      version: 1,
      applicationId: readText(pointer.applicationId) || null,
      currentStep: clampParentFinancialAidStep(pointer.currentStep),
      updatedAt: new Date().toISOString(),
    };
    storage.setItem(storageKey, JSON.stringify(record));
  } catch {
    // Non-sensitive navigation continuity is best-effort only.
  }
};

// Attestation continuity keys live under the pointer prefix on purpose:
// every existing cleanup path (successful submit, logout) already clears
// the pointer prefix, so stored attestations can never outlive the draft.
export const PARENT_FINANCIAL_AID_ATTESTATION_STORAGE_PREFIX =
  `${PARENT_FINANCIAL_AID_POINTER_STORAGE_PREFIX}:attestations`;

export type ParentFinancialAidStoredAttestations = Record<string, boolean>;

type ParentFinancialAidAttestationRecord = {
  version: 1;
  attestations: ParentFinancialAidStoredAttestations;
  updatedAt: string;
};

export const resolveParentFinancialAidAttestationStorageKey = (applicationId: string): string =>
  `${PARENT_FINANCIAL_AID_ATTESTATION_STORAGE_PREFIX}:${readText(applicationId) || 'unknown-application'}`;

const readCheckedAttestationEntries = (
  attestations: Record<string, unknown> | null | undefined,
): ParentFinancialAidStoredAttestations => Object.fromEntries(
  Object.entries(attestations ?? {})
    .filter(([key, value]) => readText(key).length > 0 && value === true)
    .map(([key]) => [key, true]),
);

export const readParentFinancialAidStoredAttestations = (
  storage: Storage | null,
  applicationId: string,
): ParentFinancialAidStoredAttestations | null => {
  if (!storage || !readText(applicationId)) return null;
  try {
    const raw = storage.getItem(resolveParentFinancialAidAttestationStorageKey(applicationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ParentFinancialAidAttestationRecord>;
    if (parsed.version !== 1) return null;
    const attestations = parsed.attestations;
    if (!attestations || typeof attestations !== 'object' || Array.isArray(attestations)) return null;
    const checked = readCheckedAttestationEntries(attestations as Record<string, unknown>);
    return Object.keys(checked).length > 0 ? checked : null;
  } catch {
    return null;
  }
};

export const writeParentFinancialAidStoredAttestations = (
  storage: Storage | null,
  applicationId: string,
  attestations: Record<string, unknown> | null | undefined,
): void => {
  if (!storage || !readText(applicationId)) return;
  const storageKey = resolveParentFinancialAidAttestationStorageKey(applicationId);
  try {
    const checked = readCheckedAttestationEntries(attestations);
    if (Object.keys(checked).length === 0) {
      storage.removeItem(storageKey);
      return;
    }
    const record: ParentFinancialAidAttestationRecord = {
      version: 1,
      attestations: checked,
      updatedAt: new Date().toISOString(),
    };
    storage.setItem(storageKey, JSON.stringify(record));
  } catch {
    // Attestation continuity is best-effort only.
  }
};

export const activateParentFinancialAidEntryFocus = (
  storage: Storage | null = typeof window === 'undefined' ? null : window.sessionStorage,
  nowMs: number = Date.now(),
): void => {
  if (!storage) return;
  try {
    const record: ParentFinancialAidEntryFocusRecord = {
      version: 1,
      focused: true,
      activatedAt: nowMs,
    };
    storage.setItem(PARENT_FINANCIAL_AID_ENTRY_FOCUS_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Non-sensitive UI focus state is best-effort only.
  }
};

export const isParentFinancialAidEntryFocusActive = (
  storage: Storage | null = typeof window === 'undefined' ? null : window.sessionStorage,
  nowMs: number = Date.now(),
): boolean => {
  if (!storage) return false;
  try {
    const raw = storage.getItem(PARENT_FINANCIAL_AID_ENTRY_FOCUS_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<ParentFinancialAidEntryFocusRecord>;
    const activatedAt = Number(parsed.activatedAt);
    const active = parsed.version === 1
      && parsed.focused === true
      && Number.isFinite(activatedAt)
      && activatedAt > 0
      && nowMs - activatedAt <= ENTRY_FOCUS_MAX_AGE_MS;
    if (!active) {
      storage.removeItem(PARENT_FINANCIAL_AID_ENTRY_FOCUS_STORAGE_KEY);
    }
    return active;
  } catch {
    return false;
  }
};

export const clearParentFinancialAidPointerStorageFrom = (storage: Storage | null): void => {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (
        key
        && (
          key.startsWith(PARENT_FINANCIAL_AID_POINTER_STORAGE_PREFIX)
          || key.startsWith(LEGACY_PARENT_FINANCIAL_AID_DRAFT_STORAGE_PREFIX)
          || key === PARENT_FINANCIAL_AID_ENTRY_FOCUS_STORAGE_KEY
        )
      ) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Ignore storage failures during logout/local-auth cleanup.
  }
};
